import { analyticsPageName, getPageVariable } from './helpers';
import { pushDataLayerEvent } from './data-layer';

// A bfcache restore keeps the page's listeners but fires the page load again.
let globalEventListenersInitialized = false;
// Launch keeps its page variables between loads on one document and fills
// pageName only while it is empty, so a later load has to name itself.
let adobePageLoadSent = false;

export const initGlobalEventListeners = async () => {
  // USE THIS METHOD TO SETUP PAGES THAT AREN'T BUILT WITH LIT ELEMENT
  if (globalEventListenersInitialized) return;
  globalEventListenersInitialized = true;

  // Global Search Page Events
  const globalSearchWrapper = document.querySelector('#global-search-results');
  if (globalSearchWrapper) setupGlobalSearchTracking(globalSearchWrapper);

  // CEUS Page
  const ceusPageHeader = document.querySelector('#ceus-page-header');
  if (ceusPageHeader) setupCEUsPageTracking(ceusPageHeader);

  // Bitreel Digital Print
  const bitreelDigitalPrintWrapper = document.querySelector(
    '#bitreel-digital-print'
  );
  if (bitreelDigitalPrintWrapper)
    setupBitreelDigitalPrintPageTracking(bitreelDigitalPrintWrapper);
};

export const adobeAnalyticsPageLoad = async detail => {
  // parse pagename values and set them on the window.analytics object
  const pageNameString = await analyticsPageName();
  // A page Adobe names differently from GA4 sets adobePageName as well.
  const adobePageNameString =
    getPageVariable('adobePageName') || pageNameString;
  if (adobePageNameString) {
    adobePageNameString.split('|').forEach((value, i) => {
      window.analytics.site[`sitesection${i === 0 ? '' : i + 1}`] = value;
    });
  }

  detail = !!detail ? detail : {};

  const sitePageTitle = analyticsSitePageTitle();
  if (adobePageLoadSent && sitePageTitle) {
    detail.raw = { ...detail.raw, pageName: sitePageTitle };
  }
  adobePageLoadSent = true;

  // trigger page load event
  const eventData = {
    type: 'load',
    detail,
  };

  // Adobe Analytics global search handling
  const globalSearchWrapper = document.querySelector('#global-search-results');
  if (globalSearchWrapper) {
    // let Adobe know a search happened on page load
    const wrapperData = JSON.parse(globalSearchWrapper.dataset.results);
    const resultsFound = !!wrapperData.results_found;
    const term = adobeCleanString(wrapperData.search_term);
    eventData.detail['search'] = {
      term,
      results: resultsFound,
    };
  }

  fireAdobeAnalyticsEvent(eventData);

  // Container tags trigger off this rather than container load, so the page
  // name is guaranteed present by the time they read it.
  // page_name is the GA4 spelling; pageName stays because the parent's
  // container variables are not visible from here and may still read it.
  pushDataLayerEvent(
    'page_ready',
    pageNameString
      ? { page_name: pageNameString, pageName: pageNameString }
      : {}
  );

  initGlobalEventListeners();
};

// In-page view of a new page, such as a gallery project's detail. Callers
// send no click with it, which would count the view twice.
export const adobeAnalyticsVirtualPageLoad = pageNameString => {
  const site = (window.analytics.site = window.analytics.site || {});
  const sections = pageNameString.split('|');
  for (let i = 0; i < 5; i++) {
    const key = `sitesection${i === 0 ? '' : i + 1}`;
    if (sections[i]) {
      site[key] = sections[i];
    } else {
      delete site[key];
    }
  }

  adobePageLoadSent = true;
  fireAdobeAnalyticsEvent({
    type: 'load',
    detail: { raw: { pageName: analyticsSitePageTitle() } },
  });
};

// Launch's sitePageTitle data element, which it names a fresh page from: the
// site sections joined by pipes, whitespace collapsed.
const analyticsSitePageTitle = () => {
  const site = window.analytics?.site || {};
  const sections = [site.sitesection];
  for (let i = 2; i <= 5; i++) {
    if (site[`sitesection${i}`]) sections.push(site[`sitesection${i}`]);
  }
  return sections
    .join('|')
    .replace(/\s+/g, ' ')
    .trim();
};

export const updateAnalyticsObectWithCartData = cart => {
  if (cart && cart.data) {
    // set cart id
    const id = cart.data.id;
    if (id) window.analytics.cart.id = id;
  }
};

export const fireAdobeAnalyticsEvent = async (
  eventData = { detail: {} },
  target
) => {
  const { detail } = eventData,
    eventName = `analytics:${eventData.type}`;

  // Make the event using the passed in event data
  const event = new CustomEvent(eventName, {
    detail,
  });

  if (typeof event.detail === 'object') {
    // eVar70 is cleaned by video-analytics, which keeps its video ID as is.
    const leaveTheseDirty = [
      'eVar70',
      'eVar108',
      'eVar109',
      'eVar230',
      'products',
    ];
    const useHyphenDelimiter = ['eVar0', 'eVar35'];

    for (const [key, value] of Object.entries(event.detail)) {
      if (typeof value !== 'string' || stringIsUrl(value)) continue;
      if (leaveTheseDirty.includes(key)) continue;

      const useHyphen = useHyphenDelimiter.includes(key);
      event.detail[key] = adobeCleanString(value, useHyphen);
    }
  }

  // Allow these events to escape the shadowRoot by setting "composed: true"
  // To do this, we need to clone the incoming read only event so that the value
  // can be overridden. Doing it here prevents needing to add this flag
  // everywhere this method gets called.
  const customEvent = new CustomEvent(event.type, {
    detail: event.detail,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    composed: true,
  });

  if (!target) target = document.body;
  // If window.alloy is not yet available add the event to a queue that will
  // be scraped when alloy loads.
  if (window.alloy) {
    await target.dispatchEvent(customEvent);
  } else {
    window.awiEventQueue = window.awiEventQueue || [];
    window.awiEventQueue.push({
      event: customEvent,
      target: target,
    });
  }
};

export const stringIsUrl = string => {
  // This isn't a perfect test, but it's certainly good enough for any of our
  // urls that might get passed through.
  const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]{1,5})?(\/[^\s]*)?$/i;
  return urlRegex.test(string);
};

export const adobeCleanString = (str, allowHyphen = false) => {
  if (typeof str !== 'string') return str;

  // Replace the literal string "undefined" with an empty string
  str = str.replace(/undefined/g, '');

  // Replace line breaks with whitespace and remove HTML tags
  str = str.replace(/<br>/g, ' ');
  str = str.replace(/<[^>]*>/g, '');

  // Turn hyphens to white space if not allowed
  if (!allowHyphen) {
    str = str.replace(/-/g, ' ');
  }

  // Remove anything that is not alphanumeric, whitespace, pipe, or hyphen
  str = str.replace(/[^\w\s|\-]/g, '');

  // Collapse multiple spaces into one
  str = str.replace(/\s+/g, ' ');

  // Capitalize each word after the first pipe (`|`)
  str = str.replace(
    /\|(.+)/,
    (_, afterPipe) =>
      '|' +
      afterPipe.replace(
        /\b\w+/g,
        word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
  );

  return str.trim();
};

export const handleFileDownloadEvent = eventData => {
  if (!eventData) return;
  const downloadsAnalyticsDataObj =
    typeof eventData === 'string' ? JSON.parse(eventData) : eventData;

  // All fields are required, except search
  let { file_name, link_url, placement, search } = downloadsAnalyticsDataObj;

  // Tech-specs links are downloads by URL alone, so their CTA hands over its
  // nav object; name the file from the CTA rather than dropping the event.
  if (downloadsAnalyticsDataObj.isNav) {
    const ctaText =
      downloadsAnalyticsDataObj.text || downloadsAnalyticsDataObj.cta?.text;
    file_name = file_name || analyticsLabelFromString(ctaText || '');
    link_url = link_url || downloadsAnalyticsDataObj.url || '';
  }

  // Every sender lands here, so the type and format are settled here.
  const fileObj = { ...downloadsAnalyticsDataObj, file_name, link_url };
  const { adobe = {} } = downloadsAnalyticsDataObj;
  const link_type = adobe.link_type || analyticsDownloadType(fileObj);
  const file_format = adobe.file_format || analyticsDownloadFormat(fileObj);

  placement = adobeCleanString(placement || '').replace(/\|/g, ' ');
  // AA classifies none of eVar9's parts when any one of them is empty.
  file_name =
    adobeCleanString(file_name || '').replace(/\|/g, ' ') ||
    placement ||
    UNKNOWN_DOWNLOAD_PART;

  const formattedEventData = {
    type: 'download',
    detail: {
      eVar8: `FILE|${placement}|${file_name}`,
      eVar9: `${link_type}|${file_name}|${file_format}`,
      eVar109: link_url,
    },
  };

  if (typeof search === 'object') {
    formattedEventData.detail.search = search;
  }

  fireAdobeAnalyticsEvent(formattedEventData);
};

export const handleAdobeNavEvent = async navObj => {
  if (!navObj) return;
  navObj = isValidJsonString(navObj) ? JSON.parse(navObj) : navObj;
  const event = navObj.event || null;
  const target = event ? event.target : null;
  // currentTarget is nulled the moment dispatch ends, and the destination
  // fallback below runs after an await, so read the element's href now. Only
  // callers that suppressed the default need it: where they did not, the
  // browser is already navigating and stealing the click would break
  // cmd-click and anchors that only open a modal.
  const eventHref =
    event && event.defaultPrevented ? event.currentTarget?.href || '' : '';

  let analyticsCtaDataObject = navObj.analyticsCtaDataObject || '';
  let destinationUrl = navObj.destinationUrl || '';
  let navString = navObj.navString || '';

  if (isValidJsonString(analyticsCtaDataObject)) {
    analyticsCtaDataObject = JSON.parse(analyticsCtaDataObject);
    navString = navString || analyticsCtaDataObject.navString;
    destinationUrl = destinationUrl || analyticsCtaDataObject.destinationUrl;
  }

  const detailObj = {
    eVar8: navString,
  };

  if (navString.toLowerCase().includes('exit|')) {
    detailObj['eVar108'] = destinationUrl;
  }

  if (navString) {
    // navString == {NAV or INP}|{linkPlacement}|{linkName}
    const eventData = {
      type: 'click',
      detail: detailObj,
    };
    // Launch reads search from the detail, like every other variable.
    if (typeof navObj.search === 'object') {
      eventData.detail.search = navObj.search;
    }
    await fireAdobeAnalyticsEvent(eventData, target);
  }

  if (!navString.startsWith('INP')) {
    destinationUrl =
      typeof destinationUrl === 'string'
        ? destinationUrl || navObj.url || navObj.cta?.url || eventHref || ''
        : '';

    if (destinationUrl) {
      if (!!event) {
        event.preventDefault();
      }
      const useTargetBlank =
        navObj.target_blank ||
        navObj.target === '_blank' ||
        (destinationUrl.includes('http') &&
          !destinationUrl.includes('3-form.com'));
      setTimeout(() => {
        if (useTargetBlank) {
          window.open(destinationUrl, '_blank');
        } else {
          window.location.href = destinationUrl;
        }
      }, 150);
    }
  }
};

export const analyticsCtaDataObject = (dataObject = {}, placement = '') => {
  if (typeof dataObject === 'string') dataObject = JSON.parse(dataObject);
  let formattedAnalyticsObj = {};

  const placementStr = placement || dataObject.placement || '';
  const fileNameStr = dataObject.file_name || dataObject.text || '';
  const url = dataObject.url || dataObject.link_url;

  if (dataObject.file_format || dataObject.isDownload) {
    const fileObj = { ...dataObject, link_url: url };
    // File Download event formatting. The top-level type and format are what
    // GA4's file_download reports on; Adobe reads its own under adobe.
    formattedAnalyticsObj = {
      file_format: dataObject.file_format,
      file_name: analyticsLabelFromString(fileNameStr),
      link_type: ga4DownloadType(dataObject),
      link_url: url,
      isDownload: true,
      placement: analyticsLabelFromString(placementStr),
      adobe: {
        file_format: analyticsDownloadFormat(fileObj),
        link_type: analyticsDownloadType(fileObj),
      },
    };
  } else {
    const ctaNavName =
      dataObject.text || dataObject.cta?.text || dataObject.header;
    // Nav event formatting
    dataObject.isNav = true;
    dataObject.placement = placementStr;
    dataObject.navString = `NAV|${placementStr}|${ctaNavName}`;
    formattedAnalyticsObj = dataObject;
  }
  if (dataObject.search) formattedAnalyticsObj['search'] = dataObject.search;
  return JSON.stringify(formattedAnalyticsObj);
};

// Blocks hand their header in as the placement, so a block without one sent
// "NAV||Name". Falls back to the hosting block's analyticsBlockLabel.
export const withBlockPlacement = (analyticsData, element) => {
  const analyticsObj =
    typeof analyticsData === 'string'
      ? JSON.parse(analyticsData)
      : analyticsData;
  if (!analyticsObj || analyticsObj.placement) return analyticsObj;

  const label = element?.getRootNode?.()?.host?.analyticsBlockLabel;
  if (!label) return analyticsObj;

  analyticsObj.placement = label;
  if (typeof analyticsObj.navString === 'string') {
    analyticsObj.navString = analyticsObj.navString.replace(
      /^NAV\|\|/,
      `NAV|${label}|`
    );
  }
  return analyticsObj;
};

// eVar9 is TYPE|name|FORMAT. The type says what the file is and the format
// what it is saved as, so a file reads the same from every page that links it.
const DOWNLOAD_TYPES = ['DOCUMENT', 'IMAGE', 'CAD', 'REVIT', 'PACKAGE'];
const REVIT_FORMATS = ['RVT', 'RFA'];
const CAD_FORMATS = ['DWG', 'DXF'];
const IMAGE_FORMATS = ['JPG', 'PNG', 'GIF', 'WEBP', 'TIF', 'TIFF', 'SVG'];
// Whole words only: Cadence and Cadet are pattern names, not CAD files.
const REVIT_NAME = /(^|[^a-z])(revit|rvt|rfa)([^a-z]|$)/i;
const CAD_NAME = /(^|[^a-z])(cad|autocad|dwg|dxf)([^a-z]|$)/i;
const UNKNOWN_DOWNLOAD_PART = 'UNKNOWN';

// PACKAGE comes only from the sender. Every image counts as IMAGE, so image
// downloads read as their own type wherever they were linked. A zip named for
// neither CAD nor Revit stays ZIP.
export const analyticsDownloadType = fileObj => {
  const declaredType = String(fileObj?.link_type || '').toUpperCase();
  if (DOWNLOAD_TYPES.includes(declaredType)) return declaredType;

  const format = analyticsDownloadFormat(fileObj);
  const name = `${fileObj?.file_name || ''} ${fileObj?.text || ''}`;

  if (REVIT_FORMATS.includes(format) || REVIT_NAME.test(name)) return 'REVIT';
  if (CAD_FORMATS.includes(format) || CAD_NAME.test(name)) return 'CAD';
  if (IMAGE_FORMATS.includes(format)) return 'IMAGE';
  if (format === 'ZIP') return 'ZIP';
  return 'DOCUMENT';
};

// GA4's file_download link_type, kept to the values its reports were built on.
const ga4DownloadType = fileObj => {
  if (fileObj.link_type) {
    return fileObj.link_type;
  }

  if (fileObj?.text?.toLowerCase().includes('revit')) return 'REVIT';
  if (fileObj?.text?.toLowerCase().includes('cad')) return 'CAD';
  if (fileObj?.file_format) return fileObj.file_format;
  if (fileObj?.src && fileObj?.src.includes('image')) return 'IMAGE';
  return fileObj?.text || '';
};

// The format a technical document declares, else the URL's extension. A
// tech-specs path has neither, so it reports UNKNOWN rather than nothing.
export const analyticsDownloadFormat = fileObj => {
  const format =
    String(fileObj?.file_format || '')
      .trim()
      .replace(/^\./, '')
      .toUpperCase() ||
    downloadExtensionFromUrl(fileObj?.link_url || fileObj?.url);

  if (format === 'JPEG') return 'JPG';
  return format || UNKNOWN_DOWNLOAD_PART;
};

const downloadExtensionFromUrl = url => {
  if (!url) return '';
  let pathname = '';
  try {
    pathname = new URL(url, window.location.href).pathname;
  } catch {
    return '';
  }
  const match = pathname.match(/\.([a-z0-9]{2,5})$/i);
  return match ? match[1].toUpperCase() : '';
};

export const isValidJsonString = str => {
  if (typeof str !== 'string') return false;
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && typeof parsed === 'object';
  } catch {
    return false;
  }
};

export const analyticsLabelFromString = (str = '') => {
  // we need some way to extract meaningful data from image descriptions and
  // potentially long block titles
  // an example str would be:
  // Material | Varia<br>Interlayer | Rust Oxide<br>Hardware Finish | Carbide
  if (str.includes('<br>')) {
    if (str.includes('|')) {
      const matches = [...str.matchAll(/\|\s*(.*?)<br>?/g)].map(m => m[1]);
      return matches.join(' ');
    } else {
      return str.split('<br>')[0]; // Return everything before the first <br>
    }
  }
  // example output would be "Varia Rust Oxide Carbide"
  return truncatedAnalyticsString(str); // If neither <br> nor | exist, return the full string
};

export const truncatedAnalyticsString = (str, maxLength = 50) => {
  return str.slice(0, maxLength);
};

const setupGlobalSearchTracking = async globalSearchWrapper => {
  const wrapperData = JSON.parse(globalSearchWrapper.dataset.results);
  const resultsFound = !!wrapperData.results_found;
  const term = adobeCleanString(wrapperData.search_term);
  // setup search result click handler
  document
    .querySelector('#global-search-results')
    .addEventListener('click', function(event) {
      const target = event.target.closest('.js-search-result');
      if (target) {
        const resultData = JSON.parse(target.dataset.result);
        const linkTitle = resultData.link_title
          .replace('|', '')
          .replace('  ', ' ')
          .trim();
        const position = resultData.index + 1;
        const destinationUrl = target.getAttribute('href');

        handleAdobeNavEvent({
          navString: `NAV|Site Search|${linkTitle}`,
          destinationUrl,
          search: {
            term,
            position,
            results: resultsFound,
            toolName: 'Site Search',
          },
          event,
        });
      }
    });
};

const setupCEUsPageTracking = async ceusPageHeader => {
  const trackedLinks = document.querySelectorAll(
    'a.js-analytics-nav[data-analytics_string]'
  );
  const trackedINPs = document.querySelectorAll(
    'a.js-analytics-inp[data-analytics_string]'
  );

  trackedINPs.forEach(link => {
    link.addEventListener('click', () => {
      const label = link.getAttribute('data-analytics_string');
      const inpEventData = {
        type: 'click',
        detail: {
          eVar8: adobeCleanString(label),
        },
      };
      fireAdobeAnalyticsEvent(inpEventData);
    });
  });

  trackedLinks.forEach(link => {
    link.addEventListener('click', event => {
      const label = link.getAttribute('data-analytics_string');
      const destinationUrl = link.getAttribute('href');
      event.preventDefault();
      handleAdobeNavEvent({
        navString: label,
        destinationUrl,
        event,
      });
    });
  });
};

const setupBitreelDigitalPrintPageTracking = async bitreelDigitalPrintWrapper => {
  document.addEventListener(
    'bitreel-digital-print-download-configured-image',
    event => {
      // incoming detail: { format, background, resolution, isCustomImage }
      const detail = event.detail || {};
      const { format, background, resolution, isCustomImage } = detail;
      const customImageStr = isCustomImage ? 'User Provided' : '3form Library';
      const file_name = `Rendered Image ${customImageStr} ${background} ${resolution}`;
      const downloadDetails = {
        file_format: detail.format,
        file_name,
        link_url: 'dynamically created img',
        link_type: 'IMAGE',
        placement: 'Digital Print Configurator',
      };
      handleFileDownloadEvent(downloadDetails);
    }
  );

  document.addEventListener(
    'bitreel-digital-print-download-source-image',
    event => {
      // incoming detail: { filename, imageUrl, isCustomImage }
      const detail = event.detail || {};
      const { filename, imageUrl } = detail;
      const downloadDetails = {
        file_format: detail.format,
        file_name: filename,
        link_url: imageUrl,
        link_type: 'IMAGE',
        placement: 'Digital Print Configurator',
      };
      handleFileDownloadEvent(downloadDetails);
    }
  );
};

// Shared navigate-or-download click for image/driver media: NAV event when
// the item links somewhere, file-download event when it only carries a
// full-resolution image. Payload shape matches Lit3formBlock#handleMediaClick.
export const handleMediaItemClick = (
  item,
  event,
  { navPlacement = '', downloadPlacement = '' } = {}
) => {
  if (item.url) {
    handleAdobeNavEvent({
      navString: `NAV|${analyticsLabelFromString(
        navPlacement
      )}|${analyticsLabelFromString(item.title || item.text)}`,
      destinationUrl: item.url,
      event,
    });
  } else if (item.full_resolution_src) {
    handleFileDownloadEvent(
      analyticsCtaDataObject({
        file_format: 'JPG',
        file_name: analyticsLabelFromString(item.title || downloadPlacement),
        link_url: item.full_resolution_src,
        link_type: 'IMAGE',
        placement: analyticsLabelFromString(downloadPlacement),
      })
    );
  }
};
